import type { ProjectedStep, StepDisplayState } from "../../../../api/threads/liveMessages";

/**
 * What the current step offers (F33, R-4): Run when Ready, a question when it needs input, the
 * person's answer, the approval it waits for, Try again when it failed, nothing while it runs.
 * Module C decides which step is current and its state (W-22, `current_step`); this only
 * chooses what the step offers for it, and module B's plan may still hold a Ready step at
 * Needs input when a value does not fit.
 */
export type CurrentStepState = "needsInput" | "ready" | "waitingForYou" | "waitingForApproval" | "running" | "failed";

export interface CurrentStep {
  readonly stepKey: string;
  /** 1-based, in the Workflow's order. */
  readonly position: number;
  readonly state: CurrentStepState;
  /** Its state as module C shows it: the word its chip says, the same as on the graph. */
  readonly shown: StepDisplayState;
}

export interface StepFocus {
  /** `null` when no step is current: the Workflow is done, left, or not reached any step. */
  readonly current: CurrentStep | null;
}

export interface StepFocusFacts {
  /** The Workflow's step keys, in order. */
  readonly order: readonly string[];
  /** The step the Run is at, as module C publishes it (`projection.current_step`). */
  readonly current: ProjectedStep | null;
  /** Module B's plan names a reason the step's values don't fit (ADR-0032 D7). */
  readonly planLacksInput: (stepKey: string) => boolean;
}

/** What a current step offers in each state C shows; a settled or upcoming step is never current. */
const OFFERED: Readonly<Record<StepDisplayState, CurrentStepState | null>> = {
  ready: "ready",
  needs_input: "needsInput",
  input: "waitingForYou",
  approval: "waitingForApproval",
  offline: "running",
  slot: "running",
  queued: "running",
  running: "running",
  failed: "failed",
  upcoming: null,
  done: null,
  skipped: null,
};

/** The current step C names, with what it offers. Pure, so each state is tested in one place. */
export function stepFocus({ order, current, planLacksInput }: StepFocusFacts): StepFocus {
  const offered = current === null ? null : OFFERED[current.display_state];
  const position = current === null ? -1 : order.indexOf(current.step_key);
  if (current === null || offered === null || position < 0) return { current: null };
  const state = offered === "ready" && planLacksInput(current.step_key) ? "needsInput" : offered;
  return { current: { stepKey: current.step_key, position: position + 1, state, shown: current.display_state } };
}

/**
 * The current step as its changed values leave it (ADR-0032 D7): while values are changed,
 * a Ready or Needs input step is what module B's plan for those values says, Ready only with
 * no reason left; `pending` while that answer has not come, so Run waits for it.
 */
export function withDraft(current: CurrentStep | null, drafting: boolean, draftReasons: number | null): { readonly current: CurrentStep | null; readonly pending: boolean } {
  if (current === null || !drafting || (current.state !== "ready" && current.state !== "needsInput")) {
    return { current, pending: false };
  }
  if (draftReasons === null) return { current, pending: true };
  return { current: { ...current, state: draftReasons === 0 ? "ready" : "needsInput" }, pending: false };
}
